import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DeviceMeasurement} from "../../../../../model/device-measurement";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ActionButton} from "../../../../../model/ui/action-button";
import {PreviousRouteService} from "../../../../../services/previous-route.service";
import {noSpecialCharacters} from "../../../../../shared/custom-validators";
import {DefaultMeasurementType} from "../../../../../model/default-measurement-type";
import {HwCatalogService} from "../../../../../services/hw-catalog.service";
import {DefaultMeasurementTypesService} from "../../../../../services/default-measurement-types.service";
import {DefaultDeviceCatalog} from "../../../../../model/default-device-catalog";

@Component({
  selector: 'app-default-devices-form-measurements',
  templateUrl: './default-devices-form-measurements.component.html',
  styleUrls: ['./default-devices-form-measurements.component.scss']
})
export class DefaultDevicesFormMeasurementsComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() type: string;
  @Input() deviceMeasurements: DeviceMeasurement[];
  @Input() deviceExpressions: DeviceMeasurement[];
  @Output() deviceMeasurementsChange = new EventEmitter<DeviceMeasurement[]>();
  @Output() deviceExpressionsChange = new EventEmitter<DeviceMeasurement[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  defaultDeviceMeasurements: DeviceMeasurement[];
  defaultDeviceMeasurementsForm: FormGroup;
  rowsForm: FormArray;

  defaultMeasurementTypes: DefaultMeasurementType[];

  displayCopyFromDefaultDevice: boolean;
  copyFromDefaultDeviceActions: ActionButton[];

  constructor(private hwCatalogService: HwCatalogService,
              private defaultMeasurementTypesService: DefaultMeasurementTypesService,
              private formBuilder: FormBuilder,
              private previousService: PreviousRouteService) {
  }

  ngOnInit(): void {
    this.defaultDeviceMeasurements = this.deviceMeasurements;
    this.buildCopyFromDefaultDeviceActions();
    this.loadAndCreateForm();
  }

  private loadAndCreateForm(): void {
    this.defaultMeasurementTypesService.getDefaultMeasurementTypes().subscribe(response => {
      this.defaultMeasurementTypes = response.defaultMeasurementTypes
        .sort((a, b) => a.sensor > b.sensor ? 1 : -1);
      this.createForm(this.defaultDeviceMeasurements);
    });
  }

  private createForm(defaultDeviceMeasurements: DeviceMeasurement[]): void {
    this.rowsForm = this.formBuilder.array(
      defaultDeviceMeasurements.map(measurement => this.formGroupFromDeviceMeasurement(measurement))
    );
    this.defaultDeviceMeasurementsForm = this.formBuilder.group({
      defaultDeviceMeasurements: this.rowsForm
    });
  }

  addMeasurement(): void {
    const newMeasurement = this.formGroupFromDeviceMeasurement(new DeviceMeasurement());
    this.rowsForm.push(newMeasurement);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
  }

  deleteAllRows(): void {
    while (this.rowsForm.length > 0) {
      this.rowsForm.removeAt(0);
    }
  }

  onPreviousStep(): void {
    const measurements = this.defaultDeviceMeasurementsForm.get('defaultDeviceMeasurements').value;
    this.previousStep.emit();
    this.deviceMeasurementsChange.emit(measurements);
  }

  onNextStep(): void {
    if (this.defaultDeviceMeasurementsForm.valid) {
      const measurements = this.defaultDeviceMeasurementsForm.get('defaultDeviceMeasurements').value;
      this.nextStep.emit();
      this.deviceMeasurementsChange.emit(measurements);
      this.deviceExpressionsChange.emit(this.deviceExpressions);
    }
  }

  private formGroupFromDeviceMeasurement(measurement: DeviceMeasurement): FormGroup {
    return this.formBuilder.group({
      name: [measurement.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      description: [measurement.description, [Validators.maxLength(255)]],
      alias: [measurement.alias],
      measurementType: [measurement.measurementType, [Validators.required]],
      sampleRate: [measurement.sampleRate, [Validators.min(1)]],
      expression: [measurement.expression],
      uuid: [measurement.uuid]
    }, {updateOn: 'blur'});
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

  private buildCopyFromDefaultDeviceActions(): void {
    this.copyFromDefaultDeviceActions = [
      {
        label: 'misc.copySensors',
        icon: 'pi pi-copy',
        action: (defaultDevice: DefaultDeviceCatalog) => this.copyMeasurements(defaultDevice)
      }, {
        label: 'misc.appendSensors',
        icon: 'pi pi-plus',
        action: (defaultDevice: DefaultDeviceCatalog) => this.copyMeasurements(defaultDevice, true)
      }
    ];
  }

  copyMeasurements(defaultDevice: DefaultDeviceCatalog, append: boolean = false): void {
    if (!append) {
      this.deleteAllRows();
    }
    const measurements = this.defaultDeviceMeasurementsForm.get('defaultDeviceMeasurements').value;
    this.hwCatalogService.getDefaultDeviceCatalog(defaultDevice.uuid).toPromise().then(response => {
      let fullDefaultDevice = response.defaultDeviceCatalog;
      if (fullDefaultDevice.content?.deviceMeasurements) {
        fullDefaultDevice.content.deviceMeasurements.forEach((measurement: DeviceMeasurement) => {
          measurement.uuid = null;
          if (measurements.find(sensor => sensor.name === measurement.name) === undefined &&
            this.deviceExpressions.find(sensor => sensor.name === measurement.name) === undefined) {
            if (!measurement.expression) {
              this.rowsForm.push(this.formGroupFromDeviceMeasurement(measurement));
            } else {
              this.deviceExpressions.push(measurement);
            }
          }
        });
      }
      this.displayCopyFromDefaultDevice = false;
    });
  }

}
